import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Search,
  Filter,
  Download,
  User,
  Mail,
  Calendar,
  Shield,
  CheckCircle,
  XCircle,
  Users,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Wallet,
  CreditCard,
  AlertCircle,
  Loader2,
  Phone,
  DollarSign,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Zap,
  ChevronDown,
  UserPlus,
} from "lucide-react";
import { toast } from "react-toastify";
import WalletModal from "./WalletModal";

const WalletManagement = () => {
  const API_BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "";

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [openWalletModel, setOpenWalletModel] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedRows, setExpandedRows] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeWallets: 0,
    pendingRequests: 0,
    totalBalance: 0,
  });

  useEffect(() => {
    fetchAllUsers();
    fetchPendingRequests();
  }, []);

  // Fetch all users WITHOUT pagination
  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      // Remove pagination parameters to get ALL users
      const res = await axios.get(`${API_BASE}/api/user/`, {
        withCredentials: true,
        params: {
          limit: 1000, // Large number to get all users
          page: 1,
        },
      });

      if (res?.data?.success) {
        const usersList = res.data.data || [];

        // Fetch wallet status for each user
        const usersWithWallet = await Promise.all(
          usersList.map(async (user) => {
            try {
              const walletRes = await axios.get(
                `${API_BASE}/api/wallet/wallet-status/${user._id}`,
                { withCredentials: true }
              );

              if (walletRes.data.success) {
                const { isActive, balance, latestRequest } =
                  walletRes.data.data;
                return {
                  ...user,
                  walletStatus: isActive
                    ? "active"
                    : latestRequest?.status === "pending"
                    ? "pending"
                    : latestRequest?.status === "deactivated"
                    ? "deactivated"
                    : "inactive",
                  balance: balance || 0,
                  hasPendingRequest: latestRequest?.status === "pending",
                };
              }
              return {
                ...user,
                walletStatus: "inactive",
                balance: 0,
                hasPendingRequest: false,
              };
            } catch (err) {
              return {
                ...user,
                walletStatus: "inactive",
                balance: 0,
                hasPendingRequest: false,
              };
            }
          })
        );

        setUsers(usersWithWallet);
        setFilteredUsers(usersWithWallet);

        // Calculate stats
        const activeWallets = usersWithWallet.filter(
          (u) => u.walletStatus === "active"
        ).length;
        const pendingRequestsCount = usersWithWallet.filter(
          (u) => u.hasPendingRequest
        ).length;
        const totalBalance = usersWithWallet.reduce(
          (sum, u) => sum + (u.balance || 0),
          0
        );

        setStats({
          totalUsers: usersWithWallet.length,
          activeWallets,
          pendingRequests: pendingRequestsCount,
          totalBalance,
        });
      }
    } catch (error) {
      console.error("Failed to fetch users", error);
      toast.error("Failed to load users data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch pending requests for blinking effect
  const fetchPendingRequests = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/wallet/pending-requests`, {
        withCredentials: true,
      });

      if (res?.data?.success) {
        setPendingRequests(res.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch pending requests", error);
    }
  };

  // Filter users based on search and status
  useEffect(() => {
    let filtered = users;

    // Filter by search term
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.username?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.phone?.includes(search)
      );
    }

    // Filter by status
    if (filterStatus !== "all") {
      filtered = filtered.filter((user) => user.walletStatus === filterStatus);
    }

    // Sort: users with pending requests first, then by name
    filtered = filtered.sort((a, b) => {
      if (a.hasPendingRequest && !b.hasPendingRequest) return -1;
      if (!a.hasPendingRequest && b.hasPendingRequest) return 1;
      return (a.username || "").localeCompare(b.username || "");
    });

    setFilteredUsers(filtered);
  }, [search, filterStatus, users]);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "deactivated":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <CheckCircle className="w-4 h-4" />;
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "deactivated":
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  // Get user request type
  const getUserRequestType = (userId) => {
    const request = pendingRequests.find((req) => req.userId?._id === userId);
    if (!request) return null;

    return request.requestType === "activation"
      ? "activation"
      : request.requestType === "reactivation"
      ? "reactivation"
      : request.requestType === "fund_add"
      ? "fund_add"
      : null;
  };

  // Toggle row expansion for mobile
  const toggleRowExpansion = (id) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mb-4" />
        <p className="text-lg text-gray-600">Loading wallet management...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                Global Wallet Management
              </h1>
              <p className="text-gray-600 mt-1">
                Manage and monitor all user wallets
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => {
                  fetchAllUsers();
                  fetchPendingRequests();
                }}
                className="inline-flex items-center px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors shadow-sm"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Data
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white rounded-xl p-4 shadow border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalUsers}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-50 rounded-lg">
                  <Wallet className="w-5 h-5 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Active Wallets</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.activeWallets}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Pending Requests</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.pendingRequests}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow border border-gray-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Total Balance</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ₹{stats.totalBalance?.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-6 overflow-hidden">
          <div className="p-5">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Search by username, email, or phone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilterStatus("all")}
                  className={`px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 ${
                    filterStatus === "all"
                      ? "bg-emerald-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <Users className="w-4 h-4" />
                  All Users
                </button>
                <button
                  onClick={() => setFilterStatus("active")}
                  className={`px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 ${
                    filterStatus === "active"
                      ? "bg-green-600 text-white"
                      : "bg-green-50 text-green-700 hover:bg-green-100"
                  }`}
                >
                  <Wallet className="w-4 h-4" />
                  Active
                </button>
                <button
                  onClick={() => setFilterStatus("pending")}
                  className={`px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 ${
                    filterStatus === "pending"
                      ? "bg-yellow-600 text-white"
                      : "bg-yellow-50 text-yellow-700 hover:bg-yellow-100"
                  }`}
                >
                  <Clock className="w-4 h-4" />
                  Pending
                </button>
                <button
                  onClick={() => setFilterStatus("inactive")}
                  className={`px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 ${
                    filterStatus === "inactive"
                      ? "bg-gray-600 text-white"
                      : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Shield className="w-4 h-4" />
                  Inactive
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table Container - ALL USERS (NO PAGINATION) */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
              <p className="mt-4 text-gray-600 font-medium">
                Loading wallet data...
              </p>
              <p className="text-sm text-gray-500">Please wait</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-800 to-gray-900">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-100 uppercase tracking-wider"
                      >
                        ID
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-100 uppercase tracking-wider"
                      >
                        User Details
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-100 uppercase tracking-wider"
                      >
                        Wallet Status
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-100 uppercase tracking-wider"
                      >
                        Balance
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-100 uppercase tracking-wider"
                      >
                        Pending Request
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-100 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <User className="w-12 h-12 text-gray-400 mb-3" />
                            <p className="text-gray-900 font-medium">
                              No users found
                            </p>
                            <p className="text-gray-500 text-sm mt-1">
                              Try adjusting your filters or search terms
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user, idx) => {
                        const requestType = getUserRequestType(user._id);
                        const hasPendingRequest = user.hasPendingRequest;

                        return (
                          <tr
                            key={user._id}
                            className={`hover:bg-gray-50 transition-colors ${
                              hasPendingRequest
                                ? "animate-pulse bg-gradient-to-r from-yellow-50/50 to-amber-50/50"
                                : ""
                            }`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 rounded-full">
                                {idx + 1}
                              </span>
                            </td>

                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-full flex items-center justify-center">
                                  <User className="w-5 h-5 text-emerald-700" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {user?.username || "N/A"}
                                    {hasPendingRequest && (
                                      <span className="ml-2 inline-flex items-center">
                                        <Zap className="w-3 h-3 text-yellow-500 animate-pulse" />
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {user?.email || "N/A"}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {user?.phone || "No phone"}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${getStatusColor(
                                  user.walletStatus
                                )}`}
                              >
                                {getStatusIcon(user.walletStatus)}
                                {user.walletStatus.charAt(0).toUpperCase() +
                                  user.walletStatus.slice(1)}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <DollarSign className="w-4 h-4 text-emerald-600" />
                                <span className="font-semibold text-gray-900">
                                  ₹{user.balance?.toLocaleString() || "0"}
                                </span>
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              {requestType ? (
                                <div
                                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                                    requestType === "activation" ||
                                    requestType === "reactivation"
                                      ? "bg-blue-100 text-blue-800 border border-blue-200"
                                      : "bg-purple-100 text-purple-800 border border-purple-200"
                                  }`}
                                >
                                  {requestType === "activation" ||
                                  requestType === "reactivation" ? (
                                    <UserPlus className="w-3 h-3" />
                                  ) : (
                                    <CreditCard className="w-3 h-3" />
                                  )}
                                  {requestType === "activation"
                                    ? "Activation"
                                    : requestType === "reactivation"
                                    ? "Reactivation"
                                    : "Fund Add"}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => {
                                  setSelectedUser(user);
                                  setOpenWalletModel(true);
                                }}
                                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all shadow-sm hover:shadow-md"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Manage Wallet
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Show total count instead of pagination */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-semibold">
                      {filteredUsers.length}
                    </span>{" "}
                    of <span className="font-semibold">{users.length}</span>{" "}
                    users
                  </div>
                  <div className="text-sm text-gray-500">
                    {filteredUsers.length === users.length
                      ? "All users are displayed"
                      : `${filteredUsers.length} users match your filters`}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Wallet Modal */}
      <WalletModal
        open={openWalletModel}
        onClose={() => {
          setOpenWalletModel(false);
          setSelectedUser(null);
          fetchAllUsers(); // Refresh data after closing modal
          fetchPendingRequests();
        }}
        user={selectedUser}
        refreshRequests={() => {
          fetchAllUsers();
          fetchPendingRequests();
        }}
      />
    </div>
  );
};

export default WalletManagement;
